import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FieldAddonDropdown, FieldDatePicker, FieldDateRangePicker, FieldSelect, FieldSlider, FieldText, FlexBox, FormField, FormGrid, IconButton, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { Field } from 'redux-form';
import { getSystemVariableString, SystemVariableId, SystemVariableType, } from 'components/powerFlowShared/helpers/flowVariableHelpers';
import { useHasFeature } from 'components/shared/featureFlag';
import { FlowType } from 'fable/flowEditor';
import { MatchingOperator } from '../../types';
import styles from './criteriaFields.module.scss';
import { getInputPlaceholder, getValidOperator, getVariableItems, } from './util/helper';
const FieldCustom = Field;
export var MatchIdType;
(function (MatchIdType) {
    MatchIdType["Date"] = "Date";
    MatchIdType["DateRange"] = "Date range";
    MatchIdType["Input"] = "Input";
    MatchIdType["ScopeVariable"] = "Variable";
    MatchIdType["SpamProbabilityVariable"] = "Spam Threshold";
})(MatchIdType || (MatchIdType = {}));
const dateVariable = { id: 'date', name: MatchIdType.Date };
const dateRangeVariable = { id: 'dateRange', name: MatchIdType.DateRange };
const inputVariable = { id: 'input', name: MatchIdType.Input };
const scopeVariable = { id: 'scope', name: MatchIdType.ScopeVariable };
const spamProbabilityVariable = {
    id: 'spam_probability',
    name: MatchIdType.SpamProbabilityVariable,
};
const systemDateVariable = getSystemVariableString(SystemVariableId.Date);
const systemSpamVariable = getSystemVariableString(SystemVariableId.SpamProbability);
const getVariableTypeItems = (variable, flowType) => {
    const isDateVariableSelected = variable === systemDateVariable;
    const isSpamVariableSelected = variable === systemSpamVariable;
    const isMessengerFlow = flowType === FlowType.FLOW_TYPE_MESSENGER;
    const isScopeVariableSelected = variable === null || variable === void 0 ? void 0 : variable.startsWith(`${SystemVariableType.Scope}:`);
    const isDateMatchShown = !isMessengerFlow && (isDateVariableSelected || isScopeVariableSelected);
    if (isSpamVariableSelected)
        return [spamProbabilityVariable];
    return [
        ...(!isDateVariableSelected ? [inputVariable, scopeVariable] : []),
        ...(isDateMatchShown ? [dateVariable, dateRangeVariable] : []),
    ];
};
const getSelectedValue = (items, variableType) => {
    const selectedItem = items.find((item) => item.name === variableType);
    if (!selectedItem || !variableType) {
        return items[0].name;
    }
    return variableType;
};
const getPrefix = (items, selectedValue, field, changeFormField) => {
    if (items.length === 1)
        return items[0].name;
    return {
        type: 'custom',
        noWrap: true,
        value: (_jsx(FieldAddonDropdown, { items: items, meta: {
                selectedValue,
            }, fixed: true, onSelect: (item) => {
                changeFormField('', `${field}.match`);
                changeFormField(item.name, `${field}.variableType`);
            } })),
    };
};
export function CriteriaFields({ flowType, changeFormField, field, index, onRemove, rules, totalRows, scopeVariables, }) {
    const currentRule = rules && rules[index];
    const hasLanguageDetectionEmail = useHasFeature('language-detection-email');
    const hasLanguageDetectionMessenger = useHasFeature('language-detection-dixa-messenger');
    const hasSpamFiltering = useHasFeature('spam-filtering');
    const variables = getVariableItems({
        flowType,
        scopeVariables,
        selectedVariable: currentRule.variable,
        featureFlags: {
            hasLanguageDetectionEmail,
            hasLanguageDetectionMessenger,
            hasSpamFiltering,
        },
    });
    const { matching_rule: currentMatchingRule, variable: currentVariable, variableType: currentVariableType, } = currentRule;
    const showMatchField = ![
        MatchingOperator.IS_EMPTY,
        MatchingOperator.IS_NOT_EMPTY,
    ].includes(currentMatchingRule);
    function renderMatchField(type) {
        const items = getVariableTypeItems(currentVariable, flowType);
        const selectedValue = getSelectedValue(items, currentVariableType);
        const matchFieldCommonProps = {
            name: `${field}.match`,
            noLabel: true,
            prefix: getPrefix(items, selectedValue, field, changeFormField),
            required: true,
        };
        switch (type) {
            case MatchIdType.SpamProbabilityVariable:
                return (_jsx(FieldCustom, Object.assign({}, matchFieldCommonProps, { component: FieldSlider, min: 0, max: 100, formatValue: (value) => `${value}%` })));
            case MatchIdType.ScopeVariable:
                return (_jsx(FieldCustom, Object.assign({}, matchFieldCommonProps, { component: FieldSelect, dropdownProps: { fixed: true }, items: variables, placeholder: "Please select a variable", searchable: true })));
            case MatchIdType.Date:
                return (_jsx(FieldCustom, Object.assign({}, matchFieldCommonProps, { component: FieldDatePicker, dropdownProps: { fixed: true }, datePickerProps: { withTimezoneSelector: true }, placeholder: "Select a date" })));
            case MatchIdType.DateRange:
                return (_jsx(FieldCustom, Object.assign({}, matchFieldCommonProps, { component: FieldDateRangePicker, dropdownProps: { fixed: true }, dateRangePickerProps: { withTimezoneSelector: true }, placeholder: "Select a date" })));
            default:
                return (_jsx(FieldCustom, Object.assign({}, matchFieldCommonProps, { component: FieldText, placeholder: getInputPlaceholder(currentVariable, currentMatchingRule) })));
        }
    }
    const rootClass = classnames({
        [styles.root]: true,
        [styles.rootFirst]: index === 0,
    });
    return (_jsxs(FlexBox, Object.assign({ className: rootClass }, { children: [_jsxs(FormGrid, { children: [_jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(FieldCustom, { component: FieldSelect, dropdownProps: {
                                fixed: true,
                            }, items: variables, name: `${field}.variable`, noAlert: true, noLabel: true, placeholder: "Select variable", required: true, onChange: (_event, newValue, previousValue) => {
                                const items = getVariableTypeItems(newValue, flowType);
                                const selectedValue = getSelectedValue(items, currentVariableType);
                                if ((newValue !== systemDateVariable &&
                                    previousValue === systemDateVariable) ||
                                    (newValue !== systemSpamVariable &&
                                        previousValue === systemSpamVariable)) {
                                    changeFormField('', `${field}.match`);
                                    changeFormField(selectedValue, `${field}.variableType`);
                                }
                                if (newValue === systemDateVariable &&
                                    previousValue !== systemDateVariable) {
                                    changeFormField('', `${field}.match`);
                                    changeFormField(MatchIdType.Date, `${field}.variableType`);
                                }
                                if (newValue === systemSpamVariable &&
                                    previousValue !== systemSpamVariable) {
                                    changeFormField(50, `${field}.match`);
                                    changeFormField(MatchIdType.SpamProbabilityVariable, `${field}.variableType`);
                                }
                                // When the variable changes, we have to align the operator,
                                // and use the first one from the list. In this case we can
                                // prevent the operator select from being empty.
                                const [firstOperator] = getValidOperator(newValue);
                                changeFormField(firstOperator.id, `${field}.matching_rule`);
                            } }) })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(Field, { required: true, component: FieldSelect, name: `${field}.matching_rule`, items: getValidOperator(currentRule.variable, currentRule.matching_rule), dropdownProps: { fixed: true }, noAlert: true, noLabel: true }) })), showMatchField && (_jsx(FormField, { children: renderMatchField(currentVariableType) }))] }), _jsx(FlexBox, Object.assign({ alignItems: "center", className: styles.trashButton }, { children: _jsx(IconButton, { icon: "trash", variant: "tertiary", type: "button", disabled: totalRows === 1, onClick: () => onRemove(index) }) }))] })));
}
